/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file periodSlice.js
 * @description Redux slice for managing period related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the period slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (periods = null, periodsBySport = {}) => ({
  error: null,
  loading: false,
  periods,
  periodsBySport,
});

/**
 * @function getPeriods
 * @description Async thunk for get periods operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getPeriods = createAsyncThunk("periods/loadPeriods", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/periods?originId=${originId}&lineId=${lineId}`);

    return {
      periods: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain period details", // serializable (err.response.data)
      name: "Periods List Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function getPeriodsBySport
 * @description Async thunk for get periods by sport operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getPeriodsBySport = createAsyncThunk("periods/loadPeriodsBySport", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/periods/bysport?originId=${originId}&lineId=${lineId}`);

    return {
      periodsBySport: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain period details", // serializable (err.response.data)
      name: "Periods List Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant periodSlice
 * @description Redux slice for period management
 */
const periodSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getPeriods.pending, (state) => {
        state.periods = null;
        state.error = null;
        state.loading = true;
      })
      .addCase(getPeriods.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getPeriods.fulfilled, (state, action) => {
        state.error = null;
        state.periods = action.payload.periods;
        state.loading = false;
      })

      .addCase(getPeriodsBySport.pending, (state) => {
        state.periodsBySport = null;
        state.error = null;
        state.loading = true;
      })
      .addCase(getPeriodsBySport.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getPeriodsBySport.fulfilled, (state, action) => {
        state.error = null;
        state.periodsBySport = action.payload.periodsBySport;
        state.loading = false;
      });
  },
  initialState: getInitialState(),
  name: "period",
  reducers: {},
});
const { reducer } = periodSlice;
export default reducer;
